import { TrendingUp, Building2, CircleDollarSign, Award } from "lucide-react";
import { FeaturedCard } from "@/components/site/FundBits";

const WHY = [
  [TrendingUp, "Growth Industries", "Our ETFs target high-growth industries and markets for potential long-term growth."],
  [Building2, "Leading Businesses", "We invest in top-tier businesses with strong financials and leadership in growth industries."],
  [CircleDollarSign, "Income Generation", "Where appropriate, we generate monthly income through a flexible covered call strategy."],
  [Award, "Track Record", "One of Canada's most experienced options writing firms with a long history of delivering strong monthly income."],
];

const STRATEGIES = [
  ["Equity-Income-ETFs-Blue", "Income Leaders", "Invests in well-established companies with strong balance sheets and consistent earnings growth.", "Explore Income Leaders™ ETFs"],
  ["Enhanced-Equity-ETFs-Blue", "Enhanced Income", "Applies modest leverage to select Harvest Equity Income ETFs for higher income and growth potential.", "Explore Enhanced Income ETFs"],
  ["Fixed-Income-ETFs-Blue", "Fixed Income", "Cover the entire maturity spectrum with short, intermediate, and long-duration Treasury bills and bonds.", "Explore Fixed Income ETFs"],
  ["Balanced-Income-ETFs-Blue", "Asset Allocation", "Diversified portfolios with exposure to high-quality equities and investment grade fixed income.", "Explore Multi-Asset ETFs"],
  ["Equity-Growth-ETFs-Blue", "Specialty", "Invests in businesses with high potential for growth, stability, or an index that will provide growth opportunities.", "Explore Specialty ETFs"],
  ["Digital-Assets-ETFs-Blue", "Digital Assets", "Next-gen currencies, growth potential from crypto and blockchain, and monthly income from covered calls and leverage.", "Explore Digital Asset ETFs"],
];

export const Hero = ({ eyebrow, title, body, cta = "Discover more", cta2 }) => (
  <section className="hv-hero on-dark text-white" data-testid="hero-section">
    <div className="mx-auto max-w-[1280px] px-6 py-24 md:py-32">
      <p className="hv-rise font-heading text-xs font-semibold uppercase tracking-[0.2em] hv-accent-text">{eyebrow}</p>
      <h1 className="hv-rise mt-5 max-w-4xl font-heading text-4xl font-bold leading-[1.08] sm:text-5xl lg:text-7xl" style={{ animationDelay: "80ms" }}>{title}</h1>
      <p className="hv-rise mt-6 max-w-2xl text-base text-white/85 md:text-lg" style={{ animationDelay: "160ms" }}>{body}</p>
      <div className="hv-rise mt-10 flex flex-wrap gap-4" style={{ animationDelay: "240ms" }}>
        <a href="#featured" className="hv-btn-dark" data-testid="hero-discover-more-button">{cta}</a>
        {cta2 && <a href="#featured" className="hv-btn-dark" data-testid="hero-secondary-button">{cta2}</a>}
      </div>
    </div>
  </section>
);

export default function HomePage() {
  return (
    <>
      <Hero eyebrow="TSX: HONE" title="The World, All-In-HONE" body="Own the worlds leading companies. Get paid every month. Available now on the TSX." cta="Press release" cta2="HONE details" />
      <section className="bg-white" aria-labelledby="why-heading">
        <div className="mx-auto max-w-[1280px] px-6 py-24">
          <h2 id="why-heading" className="font-heading text-3xl font-bold text-hv-text md:text-4xl">Why Consider Harvest ETFs?</h2>
          <p className="mt-4 max-w-3xl text-base text-hv-text">We focus on wealth creation by investing in strong, growth-oriented businesses and using covered calls for income generation.</p>
          <div className="mt-14 grid gap-12 sm:grid-cols-2 lg:grid-cols-4">
            {WHY.map(([Icon, h, p]) => (
              <div key={h}>
                <span className="hv-line-icon" aria-hidden="true"><Icon size={28} strokeWidth={2} fill="none" /></span>
                <h3 className="mt-6 font-heading text-lg font-bold text-hv-text">{h}</h3>
                <p className="mt-2 text-sm text-hv-text">{p}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      <section id="featured" className="bg-hv-section" aria-labelledby="featured-heading" data-testid="featured-etfs-section">
        <div className="mx-auto max-w-[1280px] px-6 py-24">
          <h2 id="featured-heading" className="font-heading text-3xl font-bold text-hv-text md:text-4xl">Featured ETFs</h2>
          <div className="mt-12 grid gap-8 md:grid-cols-3">
            <FeaturedCard ticker="HHL" internal="/etf/hhl/" />
            <FeaturedCard ticker="HTA" internal="/etf/hta/" />
            <FeaturedCard ticker="HDIF" internal="/etf/hdif/" />
          </div>
        </div>
      </section>
      <section className="bg-white" aria-labelledby="strategy-heading">
        <div className="mx-auto max-w-[1280px] px-6 py-24">
          <h2 id="strategy-heading" className="font-heading text-3xl font-bold text-hv-text md:text-4xl">Our Investment Strategy</h2>
          <p className="mt-4 max-w-3xl text-base text-hv-text">At Harvest, we believe that wealth is created and preserved by owning leading businesses and high-quality fixed income securities.</p>
          <div className="mt-14 grid gap-x-12 gap-y-14 sm:grid-cols-2 lg:grid-cols-3">
            {STRATEGIES.map(([icon, h, p, link]) => (
              <div key={h}>
                <img src={`/brand/${icon}.svg`} alt="" className="h-16 w-16" />
                <h3 className="mt-5 font-heading text-lg font-bold text-hv-text">{h}</h3>
                <p className="mt-2 text-sm text-hv-text">{p}</p>
                <a href="#" className="mt-4 inline-block font-heading text-[13px] font-semibold text-hv-logo-navy underline-offset-4 hover:underline">{link}</a>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
